<template>
  <article v-if="visible" :class="bannerClass" role="status">
    <div class="v2-membership-notice__icon" aria-hidden="true">
      <v-icon size="24">{{ icon }}</v-icon>
    </div>

    <div class="v2-membership-notice__content">
      <div class="v2-membership-notice__header">
        <p class="v2-membership-notice__eyebrow">Estado de tu membresía</p>
        <StatusBadge :label="statusLabel" :variant="statusVariant">
          <template #icon>
            <v-icon size="14">mdi-alert-circle-outline</v-icon>
          </template>
        </StatusBadge>
      </div>
      <h2 class="v2-membership-notice__title">{{ title }}</h2>
      <p class="v2-membership-notice__description">{{ description }}</p>
      <p v-if="isProspect" class="v2-membership-notice__secondary-description">
        Si ya has cubierto tu cuota por otro método ajeno a la plataforma, ponte en contacto con tu organización para que actualice tu estatus
      </p>
    </div>
  </article>
</template>

<script>
import StatusBadge from "@/components/memberDashboardV2/ui/StatusBadge.vue";
import { resolveStatusLabel, resolveStatusVariant } from "./homeHelpers";

export default {
  name: "MembershipStatusBanner",
  components: { StatusBadge },
  props: {
    status: { type: String, default: null },
    expirationDateLabel: { type: String, default: null },
  },
  computed: {
    visible() {
      return Boolean(this.status) && this.status !== "ACTIVE";
    },
    icon() {
      return "mdi-alert-circle";
    },
    statusLabel() {
      return resolveStatusLabel(this.status);
    },
    statusVariant() {
      return resolveStatusVariant(this.status);
    },
    isProspect() {
      return this.status === "PROSPECTO";
    },
    title() {
      return this.isProspect ? "¡Bienvenido!" : "Renueva tu membresía";
    },
    description() {
      if (this.isProspect) {
        return "Tu perfil se encuentra en estatus de Prospecto, para comenzar a gozar de los beneficios de la organización debes cubrir tu cuota de membresía.";
      }
      if (
        this.expirationDateLabel &&
        ["EXPIRED", "INACTIVO"].includes(this.status)
      ) {
        return `Tu membresía venció el ${this.expirationDateLabel}. Debes renovarla para recuperar todos tus beneficios.`;
      }
      return "Debes renovar tu membresía para reactivar tu acceso a todos los beneficios.";
    },
    bannerClass() {
      return [
        "v2-membership-notice",
        `v2-membership-notice--${this.statusVariant}`,
      ];
    },
  },
};
</script>

<style scoped>
.v2-membership-notice {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  padding: var(--space-5) var(--space-6);
  overflow: hidden;
  border: 1px solid var(--notice-border);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, var(--color-surface) 0%, var(--notice-bg) 100%);
  box-shadow: var(--shadow-card);
}

.v2-membership-notice::after {
  position: absolute;
  top: -48px;
  right: -32px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: var(--notice-accent);
  content: "";
  opacity: 0.08;
}

.v2-membership-notice--error {
  --notice-accent: var(--color-error);
  --notice-bg: var(--color-error-soft);
  --notice-border: var(--color-error-soft-2);
  --notice-icon-color: var(--color-error-on);
}

.v2-membership-notice--warning {
  --notice-accent: var(--color-warning);
  --notice-bg: var(--color-warning-soft);
  --notice-border: rgba(176, 104, 0, 0.2);
  --notice-icon-color: var(--color-warning-on);
}

.v2-membership-notice--info {
  --notice-accent: var(--color-info);
  --notice-bg: var(--color-info-soft);
  --notice-border: rgba(0, 92, 137, 0.18);
  --notice-icon-color: var(--color-info-on);
}

.v2-membership-notice--neutral {
  --notice-accent: var(--color-neutral-700);
  --notice-bg: var(--color-neutral-100);
  --notice-border: var(--color-neutral-200);
  --notice-icon-color: var(--color-surface);
}

.v2-membership-notice__icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  background: var(--notice-accent);
  color: var(--notice-icon-color);
  box-shadow: var(--shadow-sm);
}

.v2-membership-notice__content {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.v2-membership-notice__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
}

.v2-membership-notice__eyebrow {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-bold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
}

.v2-membership-notice__title {
  margin: 0 0 var(--space-2);
  color: var(--color-text);
  font-family: var(--font-headline);
  font-size: var(--fs-display-sm);
  font-weight: var(--fw-bold);
  line-height: 1.2;
}

.v2-membership-notice__description {
  max-width: 720px;
  margin: 0;
  color: var(--color-text);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-medium);
  line-height: 1.5;
}

.v2-membership-notice__secondary-description {
  max-width: 720px;
  margin: var(--space-4) 0 0;
  padding: var(--space-3) var(--space-4);
  color: var(--color-text-muted);
  font-size: var(--fs-label-sm);
  line-height: 1.4;
  border-left: 3px solid var(--notice-accent);
  background-color: var(--color-neutral-100);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

@media (max-width: 600px) {
  .v2-membership-notice {
    grid-template-columns: auto minmax(0, 1fr);
    gap: var(--space-3);
    padding: var(--space-4);
  }

  .v2-membership-notice__icon {
    width: 44px;
    height: 44px;
  }

  .v2-membership-notice__header {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-2);
  }
}
</style>
